import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { jsonFetch } from '@/lib/api/jsonFetch'
import type {
  OpenOrdersCount,
  OpenOrdersFilter,
  OpenOrdersResponse,
  ProductCoverageResponse,
} from './types'

/**
 * Primary query. Refetches whenever the filter changes (TanStack Query
 * serializes deps by value, so this works).
 */
export function useOpenOrders(filter: OpenOrdersFilter) {
  return useQuery({
    queryKey: ['open-orders', 'detailed', filter],
    queryFn: () =>
      jsonFetch<OpenOrdersResponse>('/api/open-orders/detailed', {
        method: 'POST',
        body: filter,
      }),
    staleTime: 15_000,
  })
}

export function useOpenOrdersCount() {
  return useQuery({
    queryKey: ['open-orders', 'count'],
    queryFn: () => jsonFetch<OpenOrdersCount>('/api/open-orders/count'),
    refetchInterval: 60_000,
    staleTime: 30_000,
  })
}

/**
 * Saves the internal Notes column (`sol_annotations.notes_internal`,
 * upserted server-side by `PUT /api/open-orders/annotations/{sol_id}`).
 * Patches the row directly in every cached `['open-orders','detailed', ...]`
 * query instead of invalidating — a refetch would re-run the whole report
 * and lose scroll position / in-flight filters for no benefit here.
 */
export function useUpdateOrderNotes() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ sol_id, notes_internal }: { sol_id: number; notes_internal: string }) =>
      jsonFetch(`/api/open-orders/annotations/${sol_id}`, {
        method: 'PUT',
        body: { notes_internal },
      }),
    onSuccess: (_data, vars) => {
      queryClient.setQueriesData<OpenOrdersResponse>(
        { queryKey: ['open-orders', 'detailed'], exact: false },
        (old) =>
          old && {
            ...old,
            rows: old.rows.map((r) =>
              r.sol_id === vars.sol_id ? { ...r, notes_internal: vars.notes_internal } : r,
            ),
          },
      )
    },
  })
}

/**
 * Product coverage drilldown (M11 audit Row 9). Lazy-enabled — only
 * fires when a product_id is set by the ProductCoverageDrawer being
 * opened. Coverage rarely changes intraday at the line-item level, so
 * a longer staleTime is fine; the user can hit Refresh inside the
 * drawer if they want freshness.
 */
export function useProductCoverage(productId: number | null) {
  return useQuery({
    queryKey: ['product-coverage', productId],
    queryFn: () =>
      jsonFetch<ProductCoverageResponse>(
        `/api/reports/product-coverage?product_id=${productId}`,
      ),
    enabled: productId !== null,
    staleTime: 60_000,
  })
}

/**
 * Customer autocomplete for the filter rail's customer picker.
 *
 * Uses the Orders-scoped `/api/open-orders/customers` endpoint (not
 * `/api/pricelists/search/partners`, which `PartnerPicker` hardcodes) so a
 * sales rep's picker stays limited to their own accounts, and so the ids
 * returned are the commercial parents the report's `partner_ids` filter
 * expects.
 */
export function useOrderCustomerSearch(q: string) {
  return useQuery({
    queryKey: ['open-orders', 'customers', q],
    queryFn: () =>
      jsonFetch<{ id: number; name: string }[]>(
        `/api/open-orders/customers?q=${encodeURIComponent(q)}`,
      ),
    enabled: q.trim().length > 0,
    staleTime: 60_000,
  })
}
